import { AlignJustifyIcon, FileTextIcon, LinkIcon, TriangleAlertIcon, type LucideIcon } from 'lucide-react';
import type { RuleFormData } from './RulesPanel';

interface RuleTemplatesProps {
  onSelect: (data: Partial<RuleFormData>) => void;
  onCancel: () => void;
}

function defaultStep(statusCode = 200, body = '{}') {
  return { statusCode, body, headers: {} as Record<string, string>, count: 1 };
}

const TEMPLATES: { name: string; description: string; icon: LucideIcon; data: Partial<RuleFormData> }[] = [
  {
    name: 'Static Response',
    description: 'Return a fixed response for matching requests',
    icon: FileTextIcon,
    data: {
      response: {
        steps: [defaultStep()],
        sequenceMode: 'loop',
      },
    },
  },
  {
    name: 'Sequence',
    description: 'Return different responses in order across requests',
    icon: AlignJustifyIcon,
    data: {
      response: {
        steps: [defaultStep(200, '{"step": 1}'), defaultStep(200, '{"step": 2}')],
        sequenceMode: 'loop',
      },
    },
  },
  {
    name: 'Fault Injection',
    description: 'Randomly fail a percentage of requests',
    icon: TriangleAlertIcon,
    data: {
      conditions: [{ target: 'random', operator: 'less_than', value: '30' }],
      response: {
        steps: [defaultStep(500, '{"error": "Internal Server Error"}')],
        sequenceMode: 'loop',
      },
    },
  },
  {
    name: 'Action / Webhook',
    description: 'Trigger an HTTP request or set variables when matched',
    icon: LinkIcon,
    data: {
      actions: [{
        type: 'http_request',
        config: { url: '', method: 'POST' },
        onError: 'continue',
      }],
    },
  },
];

export function RuleTemplates({ onSelect, onCancel }: RuleTemplatesProps) {
  return (
    <div>
      <h3 className="mb-3">Choose a template</h3>
      <div className="grid grid-cols-2 gap-3">
        {TEMPLATES.map((template) => (
          <button
            key={template.name}
            onClick={() => onSelect(template.data)}
            className="text-left p-3 rounded-box border border-base-300 hover:border-primary hover:bg-base-200/50 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <template.icon className="size-5 text-base-content/70" />
              <span className="font-medium text-sm">{template.name}</span>
            </div>
            <p className="text-xs text-base-content/70">{template.description}</p>
          </button>
        ))}
      </div>
      <div className="flex justify-between items-center mt-3">
        <button
          onClick={() => onSelect({})}
          className="btn btn-ghost btn-sm"
        >
          Blank Rule
        </button>
        <button
          onClick={onCancel}
          className="btn btn-ghost btn-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
